/**
 * offers.js
 * ──────────────────────────────────────────────────────────────────
 * Gestion dynamique des offres promotionnelles Nexecur.
 *
 * 5 variantes (a → e) sélectionnables via le paramètre d'URL `?offer=`.
 * Si le paramètre est absent ou invalide → fallback sur `offer_a`.
 *
 * Le module injecte les contenus dans des zones marquées par des
 * attributs data dans le HTML :
 *   [data-offer-macaron]            — macaron promotionnel (hero)
 *   [data-offer-initiale-promo]     — promo carte Formule Initiale
 *   [data-offer-initiale-amount]    — prix carte Formule Initiale
 *   [data-offer-initiale-period]    — période carte Formule Initiale
 *   [data-offer-integrale-promo]    — promo carte Formule Intégrale
 *   [data-offer-integrale-amount]   — prix carte Formule Intégrale
 *   [data-offer-integrale-period]   — période carte Formule Intégrale
 *   [data-offer-legal]              — mentions légales (multilignes, innerHTML)
 *
 * Extension : ajouter une clé `offer_f` (etc.) dans `OFFERS` suffit.
 */

const DEFAULT_OFFER = 'offer_a';
const URL_PARAM = 'offer';

/**
 * Configuration des 5 variantes promotionnelles.
 *
 * Chaque offre déclare :
 *   - `macaron`   : url SVG du macaron hero
 *   - `initiale`  / `integrale` : { promo } — texte promo de la carte
 *   - `price`     : { initiale: { amount, period }, integrale: { amount, period } }
 *   - `legal`     : mentions légales en texte brut multilignes
 *
 * Toutes les valeurs sont consommées par `applyOffer` via les
 * attributs `data-offer-*` du HTML, sans aucun fallback hardcodé.
 *
 * Extension : ajouter une clé `offer_f` (etc.) dans `OFFERS` suffit.
 */
export const OFFERS = {
  offer_a: {
    macaron: "/assets/macaron_offreA.svg",
    initiale:  { promo: "Frais d'installation offerts*" },
    integrale: { promo: "Frais d'installation offerts*" },
    price: {
      initiale:  { amount: "21,90€", period: "/mois" },
      integrale: { amount: "32,90€", period: "/mois" }
    },
    legal:
      "*Frais d'installation offerts d'une valeur de 149 € TTC. Offre valable aux nouveaux clients (consommateur ou non professionnel) de Nexecur Protection pour tout engagement de 12 mois à l'une des deux formules, suite à une proposition tarifaire établie entre le 01/01/2026 et le 31/01/2026.\n\nPrestations exécutées par Nexecur Protection (contrat signé d'ordre et pour compte en agence bancaire, sur mandat confié par Nexecur Protection) SAS au capital de 12 547 360 €. Siège social : 13, rue de Belle-Ile, 72190 Coulaines. SIREN 799 869 342 RCS LE MANS. Autorisation d'exercice CNAPS AUT-072-2123-12-23-20240389180. « L'autorisation d'exercice ne confère aucune prérogative de puissance publique à l'entreprise ou aux personnes qui en bénéficient ». Pour plus de détails sur ces offres, vous pouvez vous informer auprès de votre conseiller bancaire."
  },

  offer_b: {
    macaron: "/assets/macaron_offreB.svg",
    initiale:  { promo: "2 mois d'abonnement + frais d'installation offerts*" },
    integrale: { promo: "3 mois d'abonnement + frais d'installation offerts*" },
    price: {
      initiale:  { amount: "21,90€", period: "/mois" },
      integrale: { amount: "32,90€", period: "/mois" }
    },
    legal:
      "*Détail de l'offre Ma Protection Maison : Formule Initiale 2 mois offerts, puis à partir de 21,90 € TTC/mois. Formule Intégrale : 3 mois offerts puis à partir de 32,90 € TTC/mois. Pour les deux formules : frais d'installation offerts (149 € TTC). La mensualité comprend la location du matériel et le service de télésurveillance (hors options complémentaires). Offres valables aux nouveaux clients (consommateur ou non professionnel) de Nexecur Protection pour tout engagement de 12 mois à l'une des deux formules, suite à une proposition tarifaire établie entre le 01/02/2026 et le 28/02/2026.\n\nPrestations exécutées par Nexecur Protection (contrat signé d'ordre et pour compte en agence bancaire, sur mandat confié par Nexecur Protection) SAS au capital de 12 547 360 €. Siège social : 13, rue de Belle-Ile, 72190 Coulaines. SIREN 799 869 342 RCS LE MANS. Autorisation d'exercice CNAPS AUT-072-2123-12-23-20240389180. « L'autorisation d'exercice ne confère aucune prérogative de puissance publique à l'entreprise ou aux personnes qui en bénéficient ». Pour plus de détails sur ces offres, vous pouvez vous informer auprès de votre conseiller bancaire."
  },

  offer_c: {
    macaron: "/assets/macaron_offreC.svg",
    initiale:  { promo: "2 mois d'abonnement offerts*" },
    integrale: { promo: "2 mois d'abonnement offerts*" },
    price: {
      initiale:  { amount: "21,90€", period: "/mois" },
      integrale: { amount: "32,90€", period: "/mois" }
    },
    legal:
      "*Détail de l'offre Ma Protection Maison : Formule Initiale 2 mois offerts, puis à partir de 21.90 € TTC/mois. Formule Intégrale : 2 mois offerts puis à partir de 32.90 € TTC/mois. Pour les deux formules : frais d'installation d'une valeur de 149 € TTC. La mensualité comprend la location du matériel et le service de télésurveillance (hors option complémentaires). Offres valables aux nouveaux clients (consommateur ou non professionnel) de Nexecur Protection pour tout engagement de 12 mois à l'une des deux formules, suite à une proposition tarifaire établie entre le 01/04/2026 et le 30/04/2026.\n\nPrestations exécutées par Nexecur Protection (contrat signé d'ordre et pour compte en agence bancaire, sur mandat confié par Nexecur Protection) SAS au capital de 12 547 360 €. Siège social : 13, rue de Belle-Ile, 72190 Coulaines. SIREN 799 869 342 RCS LE MANS. Autorisation d'exercice CNAPS AUT-072-2123-12-23-20240389180. « L'autorisation d'exercice ne confère aucune prérogative de puissance publique à l'entreprise ou aux personnes qui en bénéficient ». Pour plus de détails sur ces offres, vous pouvez vous informer auprès de votre conseiller bancaire."
  },

  offer_d: {
    macaron: "/assets/macaron_offreD.svg",
    initiale:  { promo: "3 mois d'abonnement + frais d'installation offerts*" },
    integrale: { promo: "6 mois d'abonnement + frais d'installation offerts*" },
    price: {
      initiale:  { amount: "21,90€", period: "/mois" },
      integrale: { amount: "32,90€", period: "/mois" }
    },
    legal:
      "*Détail de l'offre Ma Protection Maison : Formule Initiale : 3 mois offerts, puis à partir de 21,90 € TTC/mois. Formule Intégrale : 6 mois offerts, puis à partir de 32,90 € TTC/mois. Pour les deux formules : frais d'installation offerts (149 € TTC). La mensualité comprend la location du matériel et le service de télésurveillance (hors options complémentaires). Offres valables aux nouveaux clients (consommateur ou non professionnel) de Nexecur Protection pour tout engagement de 12 mois à l'une des deux formules, suite à une proposition tarifaire établie entre le 01/05/2026 et le 30/06/2026.\n\nPrestations exécutées par Nexecur Protection (contrat signé d'ordre et pour compte en agence bancaire, sur mandat confié par Nexecur Protection) SAS au capital de 12 547 360 €. Siège social : 13, rue de Belle-Ile, 72190 Coulaines. SIREN 799 869 342 RCS LE MANS. Autorisation d'exercice CNAPS AUT-072-2123-12-23-20240389180. « L'autorisation d'exercice ne confère aucune prérogative de puissance publique à l'entreprise ou aux personnes qui en bénéficient ». Pour plus de détails sur ces offres, vous pouvez vous informer auprès de votre conseiller bancaire."
  },

  offer_e: {
    macaron: "/assets/macaron_offreE.svg",
    initiale:  { promo: "1 mois d'abonnement offert*" },
    integrale: { promo: "1 mois d'abonnement offert*" },
    price: {
      initiale:  { amount: "21,90€", period: "/mois" },
      integrale: { amount: "32,90€", period: "/mois" }
    },
    legal:
      "*Détail de l'offre Ma Protection Maison : Formule Initiale : 1 mois offert, puis à partir de 21,90 € TTC/mois. Formule Intégrale : 1 mois offert, puis à partir de 32,90 € TTC/mois. Pour les deux formules : frais d'installation d'une valeur de 149 € TTC. La mensualité comprend la location du matériel et le service de télésurveillance (hors options complémentaires). Offres valables aux nouveaux clients (consommateur ou non professionnel) de Nexecur Protection pour tout engagement de 12 mois à l'une des deux formules, suite à une proposition tarifaire établie entre le 01/07/2026 et le 31/08/2026.\n\nPrestations exécutées par Nexecur Protection (contrat signé d'ordre et pour compte en agence bancaire, sur mandat confié par Nexecur Protection) SAS au capital de 12 547 360 €. Siège social : 13, rue de Belle-Ile, 72190 Coulaines. SIREN 799 869 342 RCS LE MANS. Autorisation d'exercice CNAPS AUT-072-2123-12-23-20240389180. « L'autorisation d'exercice ne confère aucune prérogative de puissance publique à l'entreprise ou aux personnes qui en bénéficient ». Pour plus de détails sur ces offres, vous pouvez vous informer auprès de votre conseiller bancaire."
  }
};

/**
 * Résout l'offre demandée à partir du paramètre `?offer=X` de l'URL.
 * Renvoie un objet décrivant la clé effective ET ce qui a été demandé,
 * afin que l'appelant puisse décider de synchroniser l'URL si besoin.
 *
 * @returns {{ key: string, requestedRaw: string | null, isValid: boolean }}
 */
export function resolveOfferFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(URL_PARAM);

  // Aucun param présent → comportement par défaut, on laisse l'URL telle quelle.
  if (raw === null) {
    return { key: DEFAULT_OFFER, requestedRaw: null, isValid: true };
  }

  // Param présent mais vide ("?offer=") → invalide, à nettoyer.
  if (raw === '') {
    return { key: DEFAULT_OFFER, requestedRaw: raw, isValid: false };
  }

  const key = `offer_${raw.toLowerCase()}`;
  const isValid = Object.prototype.hasOwnProperty.call(OFFERS, key);
  return {
    key: isValid ? key : DEFAULT_OFFER,
    requestedRaw: raw,
    isValid
  };
}

/**
 * Compat : conserve l'API initiale qui renvoie juste la clé.
 */
export function getOfferFromUrl() {
  return resolveOfferFromUrl().key;
}

/**
 * Synchronise l'URL affichée dans la barre du navigateur avec la clé
 * effectivement appliquée. Utilise history.replaceState pour ne pas
 * polluer l'historique. Ne fait rien si l'URL est déjà canonique
 * (param absent ou valeur courte minuscule correspondante).
 */
function syncUrlWithOffer(actualKey, requestedRaw, isValid) {
  // Pas de param dans l'URL d'origine → ne rien ajouter.
  if (requestedRaw === null) return;

  const short = actualKey.replace(/^offer_/, '');

  // Cas "?offer=" vide → on nettoie le param.
  if (requestedRaw === '') {
    const url = new URL(window.location.href);
    url.searchParams.delete(URL_PARAM);
    window.history.replaceState({}, '', url);
    return;
  }

  // Déjà canonique (valide ET minuscule strict) → rien à faire.
  if (isValid && requestedRaw === short) return;

  // Sinon (invalide OU majuscule, ex. "JJJJ" ou "B") → on remplace.
  const url = new URL(window.location.href);
  url.searchParams.set(URL_PARAM, short);
  window.history.replaceState({}, '', url);
}

/**
 * Convertit le texte brut multilignes (séparateurs "\n\n") en HTML
 * sécurisé : chaque paragraphe devient un <p>, le texte est échappé
 * pour éviter toute injection.
 */
function legalToHtml(text) {
  const escape = (s) =>
    s.replace(/&/g, '&amp;')
     .replace(/</g, '&lt;')
     .replace(/>/g, '&gt;')
     .replace(/"/g, '&quot;')
     .replace(/'/g, '&#39;');

  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escape(p.trim())}</p>`)
    .join('');
}

/**
 * Applique une offre : injecte ses contenus dans les zones DOM marquées
 * par des attributs `data-offer-*`. Toutes les valeurs proviennent
 * exclusivement de la configuration (aucun fallback hardcodé).
 * Tolérant aux zones manquantes (chaque sélection est testée).
 */
export function applyOffer(offerKey) {
  const offer = OFFERS[offerKey] ?? OFFERS[DEFAULT_OFFER];

  /** @type {Array<[string, string, 'text' | 'html' | 'src']>} */
  const targets = [
    ['[data-offer-macaron]',           offer.macaron,             'src'],

    ['[data-offer-initiale-promo]',    offer.initiale.promo,      'text'],
    ['[data-offer-initiale-amount]',   offer.price.initiale.amount, 'text'],
    ['[data-offer-initiale-period]',   offer.price.initiale.period, 'text'],

    ['[data-offer-integrale-promo]',   offer.integrale.promo,     'text'],
    ['[data-offer-integrale-amount]',  offer.price.integrale.amount, 'text'],
    ['[data-offer-integrale-period]',  offer.price.integrale.period, 'text'],

    ['[data-offer-legal]',             legalToHtml(offer.legal),  'html']
  ];

  for (const [selector, value, mode] of targets) {
    document.querySelectorAll(selector).forEach((el) => {
      if (mode === 'html') {
        el.innerHTML = value;
      } else if (mode === 'src') {
        el.setAttribute('src', value);
      } else {
        el.textContent = value;
      }
    });
  }

  // Expose la clé active dans <html data-offer="..."> pour styling/debug.
  document.documentElement.setAttribute('data-offer', offerKey);
}

/**
 * Initialise le système : lit l'URL, applique l'offre correspondante.
 * Appelée automatiquement (DOMContentLoaded ou immédiatement si DOM prêt).
 */
export function initOffers() {
  const run = () => {
    const { key, requestedRaw, isValid } = resolveOfferFromUrl();
    applyOffer(key);
    syncUrlWithOffer(key, requestedRaw, isValid);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}

initOffers();
